import { Role, ROLES } from '../constants/roles.constants';

export interface RouteAccessConfig {
  public?: boolean; // If true, route is accessible without authentication
  allowedRoles?: Role[]; // Roles that can access this route (only for protected routes)
}

export type RouteAccessMap = Record<string, RouteAccessConfig>;

/*
 * Route access configuration map
 * - Routes marked as `public: true` are accessible without authentication
 * - Routes with `allowedRoles` require authentication and specific roles
 * - Dynamic segments use `:paramName` syntax (e.g., /user-activate/:uuid, /route-surveys/:id)
 */
export const routeAccessConfig: RouteAccessMap = {
  // Public routes (no authentication required)
  '/login': { public: true },
  '/user-activate/:uuid': { public: true },
  '/forgot-password': { public: true },
  '/reset-password/:token': { public: true },

  // Protected routes (authentication + role-based access)
  // Reached only when the signed-in user still owes us a password change. Not public:
  // it requires a valid session, unlike the emailed activation link.
  '/set-password': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.DRIVER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.DRIVER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/dashboard': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.DRIVER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/profile': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.DRIVER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/organizations': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.DRIVER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/surveys': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.DRIVER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/organizations/add': {
    allowedRoles: [ROLES.ADMINISTRATOR, ROLES.MANAGER, ROLES.EXECUTIVE],
  },
  '/organizations/bulk-provision': {
    allowedRoles: [ROLES.ADMINISTRATOR],
  },
  '/organizations/:uuid/edit': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/organizations/:uuid/profile': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/organizations/:uuid': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/route-surveys/add': {
    allowedRoles: [ROLES.DRIVER],
  },
  '/route-surveys/:uuid': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.DRIVER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/incident-reports/add': {
    allowedRoles: [ROLES.DRIVER],
  },
  '/incident-reports/:uuid': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.DRIVER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/customer-feedbacks/add': {
    allowedRoles: [ROLES.DRIVER],
  },
  '/customer-feedbacks/:uuid': {
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.MANAGER,
      ROLES.DRIVER,
      ROLES.EXECUTIVE,
      ROLES.STAFF,
    ],
  },
  '/accounts/add': {
    allowedRoles: [ROLES.ADMINISTRATOR],
  },
  '/accounts/:uuid/edit': {
    allowedRoles: [ROLES.ADMINISTRATOR],
  },
  '/accounts/:uuid': {
    allowedRoles: [ROLES.ADMINISTRATOR],
  },
};

// INTERNAL FUNCTION: Match a pathname against a route pattern with dynamic segments
function matchRoute(pathname: string, routePattern: string): boolean {
  // Exact match
  if (pathname === routePattern) {
    return true;
  }

  // Split both paths into segments
  const pathSegments = pathname.split('/').filter(Boolean);
  const patternSegments = routePattern.split('/').filter(Boolean);

  // Different number of segments means no match
  if (pathSegments.length !== patternSegments.length) {
    return false;
  }

  // Check each segment
  for (let i = 0; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i];
    const pathSegment = pathSegments[i];

    // If pattern segment starts with ':', it's a dynamic segment - matches anything
    if (patternSegment.startsWith(':')) {
      continue;
    }

    // Static segment must match exactly
    if (patternSegment !== pathSegment) {
      return false;
    }
  }

  return true;
}

/** INTERNAL FUNCTION:
 * Find matching route pattern for a given pathname
 * Returns the route pattern key if found, undefined otherwise
 */
function findMatchingRoute(pathname: string): string | undefined {
  // Strip query parameters and hash fragments
  const cleanPathname = pathname.split('?')[0].split('#')[0];

  // Try exact match first
  if (routeAccessConfig[cleanPathname]) {
    return cleanPathname;
  }

  // Try pattern matching
  // Sort by specificity (more segments = more specific)
  const routes = Object.keys(routeAccessConfig).sort((a, b) => {
    const aSegments = a.split('/').filter(Boolean).length;
    const bSegments = b.split('/').filter(Boolean).length;
    return bSegments - aSegments;
  });

  for (const route of routes) {
    if (matchRoute(cleanPathname, route)) {
      return route;
    }
  }

  return undefined;
}

/** EXTERNAL FUNCTION USED IN __root.tsx
 * Check if a route is public
 * Supports dynamic segments (e.g., /user-activate/:uuid matches /user-activate/abc-123)
 */
export function isPublicRoute(pathname: string): boolean {
  const matchedRoute = findMatchingRoute(pathname);
  return matchedRoute
    ? routeAccessConfig[matchedRoute]?.public === true
    : false;
}

/** INTERNAL FUNCTION
 * Get allowed roles for a route
 * Supports dynamic segments (e.g., /route-surveys/:id matches /route-surveys/123)
 * Returns undefined if route is not configured or is public
 */
function getAllowedRolesForRoute(pathname: string): Role[] | undefined {
  const matchedRoute = findMatchingRoute(pathname);
  return matchedRoute
    ? routeAccessConfig[matchedRoute]?.allowedRoles
    : undefined;
}

/** EXTERNAL FUNCTION USED IN __root.tsx
 * Check if a user is authorized to access a route
 */
export function isUserAuthorizedForRoute(
  pathname: string,
  userRole: Role | null | undefined,
): boolean {
  const allowedRoles = getAllowedRolesForRoute(pathname);

  // If no allowed roles configured, route is not explicitly protected
  if (!allowedRoles) {
    return false;
  }

  // User must have a role and it must be in the allowed roles
  return userRole ? allowedRoles.includes(userRole) : false;
}
